import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { CalendarDays, CheckCircle2, Download, MapPin, Phone } from 'lucide-react';
import './BigBreakfastPage.css';
import './RestaurantOfferPage.css';

interface EmailConfirmedPageProps {
  source: 'BB10' | 'CSB10';
}

// Reached when a subscriber clicks the "confirm my email" link AWeber sends
// after the offer form is submitted (list double opt-in). The AWeber list's
// "Confirmation Success Page" must be set to this page's URL - see
// CreameryAPI/docs/aweber.md.
const EmailConfirmedPage: React.FC<EmailConfirmedPageProps> = ({ source }) =>
  source === 'BB10' ? <BreakfastEmailConfirmed /> : <RestaurantEmailConfirmed />;

const usePageTitle = (title: string) => {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = title;
    return () => { document.title = previousTitle; };
  }, [title]);
};

const BreakfastEmailConfirmed: React.FC = () => {
  usePageTitle('Email Confirmed | The Creamery Big Breakfast');

  return (
    <main className="bb-page bb-thanks">
      <div className="bb-poster">
        <header className="bb-hero">
          <img className="bb-breakfast" src="/images/food/breakfast-full.webp" alt="The Creamery's cooked breakfast with sausages, bacon, egg, black pudding, beans, tomatoes and toast" loading="eager" />
          <Link to="/" className="bb-brand" aria-label="The Creamery home"><img src="/logo.webp" alt="The Creamery — Sports Bar, Restaurant, Snooker, Pool" /></Link>
        </header>

        <section className="bb-confirmation" aria-labelledby="bb-confirmed-title">
          <CheckCircle2 aria-hidden="true" className="confirmed-icon" />
          <h1 id="bb-confirmed-title" className="bb-script">Email Confirmed!</h1>
          <h2 className="bb-rule-heading">Your 10% off Big Breakfast voucher is ready</h2>
          <p className="bb-intro">Download your voucher below and bring it with you, or simply quote the code when ordering.</p>
          <a className="bb-submit bb-submit-link" href="/documents/bb10-voucher.pdf" target="_blank" rel="noopener noreferrer" download>
            <Download aria-hidden="true" /> Download your Big Breakfast voucher (PDF)
          </a>
          <p className="bb-welcome">★ We can’t wait to see you! ★</p>
          <p className="bb-team">The Creamery Team</p>
          <Link className="bb-back" to="/BB10">Back to the offer</Link>
        </section>

        <footer className="bb-footer">
          <div className="bb-contact"><span><MapPin aria-hidden="true" /> Main Street, Bainsford, Falkirk FK2 7NU</span><a href="tel:01324636330"><Phone aria-hidden="true" />01324 636330</a><Link to="/">thecreamerysportsbar.com</Link></div>
          <p className="bb-footer-motto">Good Food · Great Drinks · Friendly Vibes</p>
          <div className="bb-legal"><Link to="/privacy">Privacy Policy</Link><Link to="/terms">Terms of Service</Link></div>
        </footer>
      </div>
    </main>
  );
};

const RestaurantEmailConfirmed: React.FC = () => {
  usePageTitle('Email Confirmed | The Creamery Restaurant Offer');

  return (
    <main className="bb-page csb-page csb-thanks">
      <div className="bb-poster csb-poster">
        <header className="csb-hero">
          <Link to="/" className="csb-brand" aria-label="The Creamery home"><img src="/logo.webp" alt="The Creamery — Sports Bar, Restaurant, Snooker, Pool" /></Link>
        </header>

        <section className="csb-confirmation" aria-labelledby="csb-confirmed-title">
          <CheckCircle2 aria-hidden="true" className="confirmed-icon" />
          <h1 id="csb-confirmed-title">★ Email confirmed! ★</h1>
          <p className="csb-brush csb-all-set">Your voucher is ready</p>
          <p className="bb-intro">Download your 10% off voucher below and bring it with you, or simply quote the code when ordering.</p>
          <a className="bb-submit bb-submit-link" href="/documents/csb10-voucher.pdf" target="_blank" rel="noopener noreferrer" download>
            <Download aria-hidden="true" /> Download your meal offer voucher (PDF)
          </a>
          <Link className="bb-back" to="/CSB10">Back to the offer</Link>
        </section>

        <footer className="csb-footer">
          <div className="csb-info">
            <div><CalendarDays aria-hidden="true" /><p>Valid for a limited time<em>Not to be used in conjunction with any other offers</em></p></div>
            <div><MapPin aria-hidden="true" /><p>Main Street,<br />Bainsford,<br />Falkirk FK2 7NU</p></div>
            <a href="tel:01324636330"><Phone aria-hidden="true" /><span>Tel:<br />01324 636330</span></a>
          </div>
          <div className="csb-opening"><h2>★ Restaurant open ★</h2><p><span>Sunday – Friday <b>12pm – 7pm</b></span><span>Saturday <b>12pm – 7.30pm</b></span></p></div>
          <img className="csb-footer-meal" src="/images/food/steak-pie1.webp" alt="Steak pie served with chips and vegetables" loading="lazy" />
          <div className="bb-legal"><Link to="/">The Creamery</Link><Link to="/privacy">Privacy Policy</Link><Link to="/terms">Terms of Service</Link></div>
        </footer>
      </div>
    </main>
  );
};

export default EmailConfirmedPage;
